"use client";

import { useLatest } from "../../hooks/use-latest";
import { sharePendingRead } from "../../lib/sharedRead";

import { ArrowLeft, CheckCircle, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSite } from "../../contexts/site-context";
import { useAuth } from "../../hooks/use-auth";
import { useResolvedAuditRouteIds } from "../../hooks/use-resolved-audit-route-ids";
import { getCachedData } from "../../lib/offlineDb.js";
import { isTemporaryEntityId } from "../../lib/syncedEntityIds";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  fetchReportWithAllData,
  selectAllReportsData,
  selectCurrentReport,
  selectCurrentReportData,
  selectCurrentReportReadOnly,
  setCurrentReport,
} from "../../redux/slices/auditReportSlice.js";
import {
  getCompletedReportsForSite,
  sectionHasImportableData,
} from "../../utils/reportSectionImport";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Footer } from "../ui/footer.jsx";
import { Progress } from "../ui/progress";

import { useSolarTemplate } from "../../contexts/solar-template-context";

import {
  useVersionAwareFiles,
  useVersionAwareKeyIssueMatrixItems,
  useVersionAwareObservations,
  useVersionAwareProjectData,
  useVersionAwareSiteData,
  useVersionAwareWorks,
} from "../../hooks/use-version-aware-data";
import { hydratePendingFileUploads } from "../../redux/slices/fileSlice.js";
import {
  initialProgressState,
  operationalConstructionAspectsProgress,
  operationalPlanningAspectsProgress,
  punchListCompletedSampleTableProgress,
  punchListConstructionProgress,
  punchListHSEManagementProgress,
  punchListKeyActivitiesPerformedProgress,
  punchListKeyActivitiesPlannedProgress,
  punchListMVSCSSLiftingProgress,
  punchListRiskMatrixProgress,
  punchListSiteProgress,
  punchListThirdPartyActivitiesProgress,
  sharedConstructionAspectsProgress,
  sharedGridConnectionProgress,
  sharedLandOwnershipProgress,
  sharedLinesPipesDrainsProgress,
  sharedObservationsProgress,
  sharedOperationalAspectsProgress,
  sharedPlanningAspectsProgress,
  sharedShadingObjectsProgress,
  sharedSiteOverviewProgress,
  sharedSiteProgress,
  templateComponents,
} from "../../utils/componentUtils.js";

export function AuditSectionContent() {
  const { siteId, reportId, section } = useResolvedAuditRouteIds();

  const {
    selectTemplate,

    selectedTemplateName,
    setSelectedTemplateName,
  } = useSolarTemplate();
  const { user, isLoading } = useAuth();
  const userId = user?._id || user?.id;
  const { currentSite = null } = useSite();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // --- Audit report / version state ---
  const [isHydratingReport, setIsHydratingReport] = useState(true);

  const [calculatedProgress, setCalculatedProgress] =
    useState<import("../../types/audit-form").SessionProgress>(
      initialProgressState,
    );

  const isReportReadOnly = useAppSelector(selectCurrentReportReadOnly);
  const allReportsData = useAppSelector(selectAllReportsData);
  const currentReportDataInStore = useAppSelector(selectCurrentReportData);
  const currentReport = useAppSelector(selectCurrentReport);
  const isSyncing = useAppSelector((state) => state.offline?.isSyncing);

  const versionProjectData = useVersionAwareProjectData(isReportReadOnly);
  const versionSiteData = useVersionAwareSiteData(isReportReadOnly);
  const versionObservationData = useVersionAwareObservations(isReportReadOnly);
  const versionKeyIssueMatrixItems =
    useVersionAwareKeyIssueMatrixItems(isReportReadOnly);
  const versionWorksData = useVersionAwareWorks(isReportReadOnly);
  const files = useVersionAwareFiles(isReportReadOnly);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [section]);

  const currentReportDataFromStore = reportId
    ? allReportsData?.[reportId] || null
    : null;
  const auditReport =
    currentReportDataFromStore?.report ||
    (currentReport?._id === reportId ? currentReport : null);
  // The active form owns live progress. Saved-data recalculations for the
  // navigation must not overwrite unsaved edits when files/records change.
  const progressScope = `${auditReport?._clientId || reportId}:${section}`;
  const [liveProgress, setLiveProgress] = useState<{
    scope: string;
    values: import("../../types/audit-form").SessionProgress;
  } | null>(null);
  const updateLiveProgress = useCallback<
    import("../../types/audit-form").SetSessionProgress
  >(
    (update) => {
      setLiveProgress((previous) => {
        const values =
          previous?.scope === progressScope
            ? previous.values
            : initialProgressState;
        const next = typeof update === "function" ? update(values) : update;
        if (previous?.scope === progressScope && next === values)
          return previous;
        return { scope: progressScope, values: next };
      });
    },
    [progressScope],
  );
  const liveSectionProgress =
    liveProgress?.scope === progressScope
      ? liveProgress.values[selectedTemplateName]?.[section]
      : undefined;
  const sessionProgress = useMemo(() => {
    if (!Number.isFinite(liveSectionProgress)) return calculatedProgress;
    return {
      ...calculatedProgress,
      [selectedTemplateName]: {
        ...calculatedProgress[selectedTemplateName],
        [section]: liveSectionProgress,
      },
    };
  }, [calculatedProgress, liveSectionProgress, selectedTemplateName, section]);

  const loadedReportId = currentReportDataFromStore?.report?._id;
  useEffect(() => {
    if (loadedReportId && loadedReportId === reportId)
      dispatch(hydratePendingFileUploads());
  }, [dispatch, loadedReportId, reportId]);
  const previousCompletedReports = useMemo(() => {
    if (!siteId || !reportId || !section) return [];

    return getCompletedReportsForSite(allReportsData, siteId, reportId).filter(
      (reportData) => sectionHasImportableData(section, reportData),
    );
  }, [allReportsData, siteId, reportId, section]);

  const auditSectionsData = useMemo(
    () =>
      auditReport?.templateId ? templateComponents[auditReport.templateId] : [],
    [auditReport?.templateId],
  );

  const updateTemplateProgress = useCallback(
    (templateName: string, updates: Record<string, number>) => {
      if (!templateName) return;

      setCalculatedProgress((prev) => {
        const current = prev[templateName] || {};
        let changed = false;
        const next = { ...current };

        Object.entries(updates).forEach(([key, value]) => {
          if (next[key] !== value) {
            next[key] = value;
            changed = true;
          }
        });

        if (!changed) return prev;
        return {
          ...prev,
          [templateName]: next,
        };
      });
    },
    [],
  );

  const updatePunchListProgress = useCallback(
    (updates: Record<string, number>) => {
      setCalculatedProgress((prev) => {
        const current = prev["punch-list"] || {};
        let changed = false;
        const next = { ...current };

        Object.entries(updates).forEach(([key, value]) => {
          if (next[key] !== value) {
            next[key] = value;
            changed = true;
          }
        });

        if (!changed) return prev;
        return {
          ...prev,
          "punch-list": next,
        };
      });
    },
    [],
  );

  useEffect(() => {
    const cachedReport =
      currentReportDataFromStore?.report ||
      (currentReport?._id === reportId ? currentReport : null);
    if (!cachedReport) return;
    if (
      cachedReport.templateId &&
      cachedReport.templateId !== selectedTemplateName
    ) {
      setSelectedTemplateName(cachedReport.templateId);
      selectTemplate(cachedReport.templateId);
    }
  }, [
    reportId,
    currentReport,
    currentReportDataFromStore,
    selectedTemplateName,
    selectTemplate,
    setSelectedTemplateName,
  ]);

  useEffect(() => {
    if (versionSiteData) {
      const newFormData = {
        topography:
          versionSiteData?.environmentalData?.siteTopography?.topography || "",
        accessToSite: versionSiteData?.accessibilityInfo?.accessToSite || "",
        visibilitySplays:
          versionSiteData?.accessibilityInfo?.visibilitySplays || "",
        accessRoadWidth:
          versionSiteData?.accessibilityInfo?.accessRoadWidth || "",
        // Description
        description: versionSiteData?.description || [],
        additionalObservations: versionSiteData?.additionalObservations || "",
      };
      // update progress whenever formData changes
      const prog = sharedSiteOverviewProgress(
        versionProjectData || {},
        files || [],
      );
      if (selectedTemplateName === "pre-construction") {
        const prog2 = sharedSiteProgress(newFormData);
        const prog3 = sharedLinesPipesDrainsProgress(
          versionSiteData?.infrastructureData || {},
        );

        const prog4 = sharedGridConnectionProgress(
          versionSiteData?.gridConnectionData || {},
        );
        const prog5 = sharedPlanningAspectsProgress(
          versionSiteData?.planningAspectsData || {},
        );
        const prog6 = sharedConstructionAspectsProgress(
          versionSiteData?.constructionAspectsData || {},
        );
        const prog7 =
          versionSiteData?.shadingObjectsData?.shadingObjectsPresent === "yes"
            ? sharedShadingObjectsProgress(
                versionSiteData?.shadingObjectsData || {},
              )
            : versionSiteData?.shadingObjectsData?.shadingObjectsPresent ===
                "no"
              ? 100
              : 0;
        const prog8 = sharedLandOwnershipProgress(
          versionSiteData?.landOwnershipData || {},
        );

        updateTemplateProgress(selectedTemplateName, {
          "site-overview": prog,
          "site-data": prog2,
          "lines-pipes-drain": prog3,
          "grid-connection-information": prog4,
          "planning-aspects": prog5,
          "construction-aspects": prog6,
          "shading-objects": prog7,
          "land-ownership": prog8,
        });
      } else if (selectedTemplateName === "under-construction") {
        const prog2 = sharedSiteProgress(newFormData);
        const prog3 = sharedLinesPipesDrainsProgress(
          versionSiteData?.infrastructureData || {},
        );

        const prog9 = sharedOperationalAspectsProgress(
          versionSiteData?.operationalAspectsData || {},
          selectedTemplateName,
        );
        updateTemplateProgress(selectedTemplateName, {
          "site-overview": prog,
          "site-data": prog2,
          "lines-pipes-drain": prog3,
          "operational-aspects": prog9,
        });
      } else if (selectedTemplateName === "operational") {
        const prog2 = sharedSiteProgress(newFormData);
        const prog3 = sharedLinesPipesDrainsProgress(
          versionSiteData?.infrastructureData || {},
        );
        const prog4 = sharedGridConnectionProgress(
          versionSiteData?.gridConnectionData || {},
        );
        const prog5 = operationalPlanningAspectsProgress(
          versionSiteData?.planningAspectsData || {},
        );
        const prog9 = sharedOperationalAspectsProgress(
          versionSiteData?.operationalAspectsData || {},
          selectedTemplateName,
        );
        updateTemplateProgress(selectedTemplateName, {
          "site-overview": prog,
          "site-data": prog2,
          "lines-pipes-drain": prog3,
          "grid-connection-information": prog4,
          "planning-aspects": prog5,
          "operational-aspects": prog9,
        });
      } else if (selectedTemplateName === "punch-list") {
        const prog2 = punchListSiteProgress(versionSiteData?.siteData || {});

        // Additional punch-list specific calculations will be done in the dedicated useEffect
        // For now, initialize with basic project and site data
        updateTemplateProgress(selectedTemplateName, {
          "site-overview": prog,
          "site-data": prog2,
        });
      }
    }
  }, [
    selectedTemplateName,
    versionSiteData,
    versionProjectData,
    files,
    updateTemplateProgress,
  ]);

  useEffect(() => {
    if (versionObservationData?.length > 0 || versionSiteData) {
      if (
        selectedTemplateName === "pre-construction" ||
        selectedTemplateName === "under-construction"
      ) {
        const prog1 = sharedObservationsProgress(
          versionObservationData?.filter(
            (obs) => obs?.observationType === "general",
          ) || [],
          versionSiteData?.punchList?.observationsSummary,
          files,
        );
        updateTemplateProgress(selectedTemplateName, {
          "overall-site-observations": prog1,
        });
      }

      if (selectedTemplateName === "operational") {
        const prog1 = operationalConstructionAspectsProgress(
          versionSiteData?.operationalData?.constructionAspects || {},
          versionObservationData.filter(
            (obs) => obs.observationType === "construction",
          ) || [],
          files,
        );
        const prog2 = sharedObservationsProgress(
          versionObservationData?.filter(
            (obs) => obs?.observationType === "general",
          ) || [],
          versionSiteData?.punchList?.observationsSummary,
          files,
        );
        updateTemplateProgress("operational", {
          "construction-aspects-operational": prog1,
          "overall-site-observations": prog2,
        });
      } else if (selectedTemplateName === "punch-list") {
        const prog3 = punchListRiskMatrixProgress(
          versionKeyIssueMatrixItems,
          files,
        );
        const prog4 = sharedObservationsProgress(
          versionObservationData?.filter(
            (obs) => obs?.observationType === "general",
          ) || [],
          versionSiteData?.punchList?.observationsSummary,
          files,
        );
        updatePunchListProgress({
          "key-issue-matrix": prog3,
          "overall-site-observations": prog4,
        });
      }
    }
  }, [
    selectedTemplateName,
    versionObservationData,
    versionKeyIssueMatrixItems,
    versionSiteData,
    files,
    updateTemplateProgress,
    updatePunchListProgress,
  ]);

  // // Punch List Template Progress Calculations
  useEffect(() => {
    const completedSampleProgress = punchListCompletedSampleTableProgress(
      versionWorksData?.filter((w) => w.workCategory === "completed-works") ||
        [],
      versionSiteData?.punchList?.completedWorksDescription,
    );
    const keyActivitiesPerformedProgress =
      punchListKeyActivitiesPerformedProgress(
        versionWorksData?.filter(
          (w) => w.workCategory === "key-activities-performed",
        ) || [],
        versionSiteData?.punchList?.keyActivitiesPerformed?.overallSummary,
      );
    const keyActivitiesPlannedProgress = punchListKeyActivitiesPlannedProgress(
      versionWorksData?.filter(
        (w) => w.workCategory === "key-activities-planned",
      ) || [],
      versionSiteData?.punchList?.keyActivitiesPlanned?.overallSummary || "",
    );
    const thirdPartyActivitiesProgress = punchListThirdPartyActivitiesProgress(
      versionWorksData?.filter(
        (w) => w.workCategory === "third-party-activities",
      ) || [],
      versionSiteData?.punchList?.thirdPartyActivities?.overallSummary || "",
      files,
    );
    const constructionProgress = punchListConstructionProgress(
      versionWorksData?.filter(
        (w) => w.workCategory === "construction-progress",
      ) || [],
      versionSiteData?.punchList?.constructionProgress?.overallSummary || "",
      files,
    );
    const liftingActivitiesProgress = punchListMVSCSSLiftingProgress(
      versionWorksData?.filter(
        (w) => w.workCategory === "lifting-activities",
      ) || [],
      versionSiteData?.punchList?.liftingActivities?.summary || "",
      files,
    );
    const hseManagementProgress = punchListHSEManagementProgress(
      versionWorksData?.filter((w) => w.workCategory === "hse-incident") || [],
      versionWorksData?.filter(
        (w) => w.workCategory === "hse-document-review",
      ) || [],
      versionSiteData?.punchList?.hseSummary || {},
      files,
    );
    if (selectedTemplateName === "punch-list") {
      updatePunchListProgress({
        "completed-sample-table": completedSampleProgress,
        "key-activities-performed": keyActivitiesPerformedProgress,
        "key-activities-planned": keyActivitiesPlannedProgress,
        "third-party-activities": thirdPartyActivitiesProgress,
        "construction-progress": constructionProgress,
        "lifting-activities": liftingActivitiesProgress,
        "hse-management": hseManagementProgress,
      });
    }
  }, [
    selectedTemplateName,
    versionSiteData,
    versionWorksData,
    files,
    updatePunchListProgress,
  ]);

  const reportLifecycle = useLatest({
    isSyncing,
    currentReportDataFromStore,
    selectedTemplateName,
    selectTemplate,
    setSelectedTemplateName,
  });

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    // An ID replacement during sync is the same loaded audit. Keep its
    // mounted form and progress visible instead of showing a cold-load screen.
    const loadedLocally =
      reportLifecycle.current.currentReportDataFromStore?.report?._id ===
      reportId;
    setIsHydratingReport(!loadedLocally);
    window.sessionStorage?.setItem("activeReportId", reportId);
    const offline =
      typeof navigator !== "undefined" && navigator.onLine === false;
    const applyReport = (report) => {
      if (cancelled || report?._id !== reportId) return;
      window.sessionStorage?.setItem("activeReportId", reportId);
      dispatch(setCurrentReport(report));
      const { selectedTemplateName, setSelectedTemplateName, selectTemplate } =
        reportLifecycle.current;
      if (report.templateId && report.templateId !== selectedTemplateName) {
        setSelectedTemplateName(report.templateId);
        selectTemplate(report.templateId);
      }
    };

    // Rehydrate the report and its section data from IndexedDB. Used both when
    // navigator.onLine reports offline, and as a fallback when the online
    // fetch below fails (navigator.onLine can misreport "online" right after
    // a cold PWA boot, which previously left the section stuck on
    // "Section not found").
    const hydrateFromCache = async () => {
      const cachedReport =
        reportLifecycle.current.currentReportDataFromStore?.report;
      if (cachedReport) {
        applyReport(cachedReport);
        return;
      }
      const records = await getCachedData("audit-reports:all-with-data");
      const record = (Array.isArray(records) ? records : []).find(
        (item) => item?.report?._id === reportId,
      );
      if (record && !cancelled) {
        dispatch({ type: "auditReports/setReportData", payload: record });
        applyReport(record.report);
      }
    };

    if (
      offline ||
      (loadedLocally && reportLifecycle.current.isSyncing) ||
      isTemporaryEntityId(reportId) ||
      isTemporaryEntityId(siteId)
    ) {
      hydrateFromCache()
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setIsHydratingReport(false);
        });
      return () => {
        cancelled = true;
      };
    }

    // The complete response already contains metadata. Share this read across
    // overlapping section mounts, scoped to the current user, site and report.
    sharePendingRead(
      dispatch,
      ["audit-section", userId, siteId, reportId],
      () => dispatch(fetchReportWithAllData({ reportId, siteId })).unwrap(),
    )
      .then((data) => {
        const record = (Array.isArray(data) ? data : [data]).find(
          (item) => item?.report?._id === reportId,
        );
        applyReport(record?.report);
      })
      .catch(() => {
        // The network request failed even though navigator.onLine reported
        // online. Fall back to cached/offline data instead of leaving the
        // section unresolved.
        return hydrateFromCache();
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsHydratingReport(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, reportId, siteId, userId, reportLifecycle]);

  // Sync cached report data into the currentReportData slice only when it differs
  useEffect(() => {
    if (currentReportDataFromStore?.report?._id) {
      // The report id can stay the same while its offline/optimistic section
      // data changes. Always refresh the current view from that cached record.
      if (currentReportDataInStore !== currentReportDataFromStore) {
        dispatch(setCurrentReport(currentReportDataFromStore.report));
      }
    }
  }, [currentReportDataFromStore, currentReportDataInStore, dispatch]);
  const config = useMemo(
    () => auditSectionsData.find((s) => s?.name === section) || null,
    [section, auditSectionsData],
  );

  if (isLoading || isHydratingReport) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-surface flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-foreground mb-2">
            Section Not Found
          </h1>
          <p className="text-slate-600 dark:text-muted-foreground mb-4">
            The requested audit section could not be found.
          </p>
          <Button onClick={() => navigate("/dashboard")}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const Icon =
    config?.icon &&
    (typeof config.icon === "function" || typeof config.icon === "object")
      ? config.icon
      : FileText;
  const FormComponent = config?.component;
  const currentSection = auditSectionsData?.find((s) => s?.name === section);
  const cachedSectionProgress =
    currentReportDataFromStore?.report?.sectionProgress ||
    currentReportDataFromStore?.report?.progressBySection ||
    {};
  const getSectionProgress = (sectionName) => {
    if (sectionName === section && Number.isFinite(liveSectionProgress))
      return liveSectionProgress;
    const sessionValue = sessionProgress?.[selectedTemplateName]?.[sectionName];
    const cachedValue = cachedSectionProgress?.[sectionName];

    if (Number.isFinite(Number(sessionValue)) && Number(sessionValue) > 0) {
      return Number(sessionValue);
    }
    return Number.isFinite(Number(cachedValue)) ? Number(cachedValue) : 0;
  };
  const sectionProgress = currentSection
    ? getSectionProgress(currentSection.name)
    : 0;
  const visibleProgressValues = auditSectionsData.map((auditSection) =>
    getSectionProgress(auditSection.name),
  );
  const overallTemplateProgress =
    visibleProgressValues.length > 0
      ? Math.round(
          visibleProgressValues.reduce((acc, value) => acc + value, 0) /
            visibleProgressValues.length,
        )
      : 0;

  // const effectiveReadOnly = Boolean(isReportReadOnly);
  const effectiveReadOnly = false;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-surface flex flex-col">
      <div className="flex flex-1 flex-col lg:flex-row">
        <aside className="w-64 bg-white dark:bg-sidebar border-r border-slate-200 dark:border-border hidden lg:block fixed left-0 top-0 h-full z-40 overflow-y-auto">
          <div className="p-4">
            <div className="mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="w-full justify-start hover:bg-slate-100 dark:hover:bg-accent"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>

            <h3 className="text-sm font-medium text-slate-700 dark:text-muted-foreground m-3">
              Audit Sections
            </h3>
            <nav aria-label="Audit sections" className="space-y-1">
              {/* Sidebar Search */}

              {Array.isArray(auditSectionsData)
                ? auditSectionsData.map((auditSection) => {
                    if (!auditSection || !auditSection.name) return null;
                    const SectionIcon = auditSection.icon || FileText;
                    const isActive = auditSection.name === section;
                    return (
                      <Button
                        title={auditSection.title || "Section"}
                        key={auditSection.name}
                        variant={isActive ? "default" : "ghost"}
                        className={`w-full justify-start h-auto p-3 ${
                          isActive
                            ? "bg-blue-600 dark:bg-sidebar-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border text-white"
                            : "hover:bg-slate-50 dark:hover:bg-accent"
                        }`}
                        onClick={() =>
                          navigate(
                            `/audit/${siteId}/${reportId}/${auditSection.name}`,
                          )
                        }
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-3 min-w-0">
                            <SectionIcon className="w-4 h-4 flex-shrink-0" />
                            <div className="text-left min-w-0">
                              <div className="text-sm font-medium truncate">
                                {auditSection.title}
                              </div>
                              <div
                                className={`text-xs ${
                                  isActive
                                    ? "text-blue-100"
                                    : "text-slate-500 dark:text-muted-foreground"
                                }`}
                              >
                                {getSectionProgress(auditSection.name)}%
                                complete
                              </div>
                            </div>
                          </div>
                          {getSectionProgress(auditSection.name) === 100 && (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                      </Button>
                    );
                  })
                : null}
            </nav>

            {currentSection && (
              <div className="mt-4 p-3 bg-slate-50 dark:bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                    Section Progress
                  </span>
                  <span className="text-sm font-medium text-slate-900 dark:text-muted-foreground">
                    {sectionProgress}%
                  </span>
                </div>
                <Progress value={sectionProgress} className="h-2" />
              </div>
            )}
          </div>
        </aside>

        <div className="lg:hidden w-full bg-white dark:bg-surface border-b border-slate-200 dark:border-border p-3 md:p-4 sticky top-0 z-30 shadow-sm">
          <div className="mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="hover:bg-slate-100 dark:hover:bg-accent h-9 px-3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm">Back to Dashboard</span>
            </Button>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {Array.isArray(auditSectionsData)
              ? auditSectionsData
                  .filter((auditSection) => auditSection?.name)
                  .map((auditSection) => {
                    const SectionIcon =
                      auditSection.icon &&
                      (typeof auditSection.icon === "function" ||
                        typeof auditSection.icon === "object")
                        ? auditSection.icon
                        : FileText;
                    const isActive = auditSection.name === section;

                    return (
                      <Button
                        title={auditSection.title || "Section"}
                        key={auditSection.name}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        className={`flex-shrink-0 h-14 min-w-[132px] px-3 md:px-4 ${
                          isActive ? "bg-blue-600 dark:bg-sidebar-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border text-white" : ""
                        }`}
                        onClick={() =>
                          navigate(
                            `/audit/${siteId}/${reportId}/${auditSection.name}`,
                          )
                        }
                      >
                        <SectionIcon className="w-4 h-4 shrink-0" />
                        <span className="flex min-w-0 flex-col items-start leading-tight">
                          <span className="max-w-[100px] truncate whitespace-nowrap text-sm">
                            {auditSection.title}
                          </span>
                          <span
                            className={`text-[11px] ${
                              isActive
                                ? "text-blue-100"
                                : "text-slate-500 dark:text-muted-foreground"
                            }`}
                          >
                            {getSectionProgress(auditSection.name)}% complete
                          </span>
                        </span>
                        {sessionProgress?.[selectedTemplateName]?.[
                          auditSection.name
                        ] === 100 && (
                          <CheckCircle className="ml-auto h-4 w-4 shrink-0 text-green-500" />
                        )}
                      </Button>
                    );
                  })
              : null}
          </div>
        </div>

        <div className="flex-1 lg:ml-64 flex flex-col">
          <div className="bg-white dark:bg-surface border-b border-slate-200 dark:border-border p-3 md:p-4 lg:p-6 sticky top-0 lg:top-0 z-20 shadow-sm">
            {/* Section title row */}
            <div className="flex items-start md:items-center gap-3 mb-3">
              <Icon className="w-5 h-5 md:w-6 md:h-6 text-blue-600 dark:text-link flex-shrink-0 mt-0.5 md:mt-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 dark:text-foreground leading-tight">
                  {config.title}
                </h1>
                <p className="text-slate-600 dark:text-muted-foreground text-sm md:text-base mt-1 leading-relaxed">
                  {config.description}
                </p>
              </div>
            </div>

            {/* Site info + version state row */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {/* Site name */}
              {versionSiteData?.siteName && (
                <Badge
                  variant="outline"
                  className="text-xs border-slate-200 dark:border-border px-2 py-1"
                >
                  {versionSiteData.siteName}
                </Badge>
              )}
              {/* Site type */}
              {versionSiteData?.siteType && (
                <Badge
                  variant="outline"
                  className="text-xs border-slate-200 dark:border-border px-2 py-1"
                >
                  {versionSiteData.siteType}
                </Badge>
              )}
              {versionSiteData?.installationType && (
                <Badge
                  variant="outline"
                  className="text-xs border-slate-200 dark:border-border px-2 py-1"
                >
                  {versionSiteData.installationType}
                </Badge>
              )}
              {/* Template name */}
              {selectedTemplateName && (
                <Badge
                  variant="outline"
                  className="text-xs border-slate-300 dark:border-primary-border px-2 py-1 text-slate-600 dark:text-muted-foreground"
                >
                  {selectedTemplateName}
                </Badge>
              )}
              {auditReport?.reportName && (
                <Badge
                  variant="outline"
                  className="text-xs border-blue-300 dark:border-primary-border px-2 py-1 text-blue-700 dark:text-link"
                >
                  {auditReport.reportName}
                </Badge>
              )}
              {effectiveReadOnly && (
                <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs px-2 py-1 border border-orange-200 dark:border-orange-700">
                  Read only
                </Badge>
              )}
              {/* Overall progress */}
              <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs px-2 py-1">
                {Number.isFinite(overallTemplateProgress)
                  ? `${overallTemplateProgress}% Overall`
                  : "0% Overall"}
              </Badge>
              {/* Current section progress */}
              {currentSection && (
                <Badge className="bg-slate-100 dark:bg-muted text-slate-700 dark:text-muted-foreground text-xs px-2 py-1">
                  {sectionProgress}% This Section
                </Badge>
              )}
            </div>

            {/* Report actions are handled on the dashboard and from the current report state. */}
          </div>

          <main className="flex-1 p-3  md:p-4 lg:p-6">
            {FormComponent ? (
              <div className="mx-auto max-w-none mt-0">
                <FormComponent
                  sessionProgress={sessionProgress || 0}
                  setSessionProgress={updateLiveProgress}
                  siteId={siteId}
                  reportId={reportId}
                  templateId={
                    auditReport?.templateId ||
                    currentReport?.templateId ||
                    selectedTemplateName
                  }
                  readOnly={effectiveReadOnly}
                  previousCompletedReports={previousCompletedReports}
                />
              </div>
            ) : (
              <Card className="w-full mx-auto bg-white dark:bg-card border-slate-200 dark:border-border">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-foreground text-lg md:text-xl">
                    <Icon className="w-5 h-5" />
                    {config?.title} - {currentSite?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 md:p-6">
                  <div className="text-center py-8 md:py-12">
                    <Icon className="w-12 h-12 md:w-16 md:h-16 text-slate-300 dark:text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground mb-2">
                      {config.title} Form Coming Soon
                    </h3>
                    <p className="text-slate-600 dark:text-muted-foreground mb-6 max-w-md mx-auto text-sm md:text-base">
                      This section will contain the {config.title.toLowerCase()}{" "}
                      form with all the necessary fields and functionality.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        variant="outline"
                        onClick={() => navigate("/dashboard")}
                        className="w-full sm:w-auto"
                      >
                        Back to Dashboard
                      </Button>
                      <Button disabled className="w-full sm:w-auto">
                        Start {config.title}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
}
